import { Drawer, ListItemIcon, ListItemText, MenuItem, MenuList, Paper, Popper } from '@mui/material'
import {
  AdminPanelSettingsOutlined,
  AssignmentIndOutlined,
  ChevronRight,
  ContentPasteOutlined,
  Inventory2Outlined,
  MenuBookOutlined,
} from '@mui/icons-material'
import { Link, useLocation } from 'react-router-dom'
import { KeyboardEvent, ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import logo from '../logo.svg'
import { useUser } from '../hooks'
import ComplianceMenu from './ComplianceMenu'
import './SideNav.css'

type FlyoutLink = { label: string; to: string }

type SideNavItem = {
  key: string
  label: string
  icon: ReactNode
  to?: string
  links?: FlyoutLink[]
  contractMenu?: boolean
}

type FlyoutState = { key: string; anchor: HTMLElement }

const focusFirstLink = (node: HTMLElement) => {
  node.querySelector<HTMLElement>('a[href]')?.focus()
}

export default function SideNav(props: { open: boolean; onClose: () => void }) {
  const location = useLocation()
  const user = useUser()
  const [flyout, setFlyout] = useState<FlyoutState | null>(null)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const flyoutNode = useRef<HTMLDivElement | null>(null)
  const focusOnOpen = useRef(false)

  const items: SideNavItem[] = [
    { key: 'quote', label: 'Quotes', icon: <ContentPasteOutlined />, to: '/quote' },
    { key: 'repcodes', label: 'Rep Codes', icon: <AssignmentIndOutlined />, to: '/repcodes' },
    {
      key: 'suppliermanagement',
      label: 'Supplier Management',
      icon: <Inventory2Outlined />,
      links: [
        { label: 'Suppliers', to: '/suppliermanagement/supplier' },
        { label: 'Manufacturers', to: '/suppliermanagement/manufacturer' },
      ],
    },
    { key: 'catalog', label: 'Contract Catalogs', icon: <MenuBookOutlined />, contractMenu: true },
    {
      key: 'imprescatalogs',
      label: 'IMPRES Catalogs',
      icon: <MenuBookOutlined />,
      links: [
        { label: 'IMPRES Catalog', to: '/imprescatalogs/impres' },
        { label: 'TD SYNNEX Catalog', to: '/imprescatalogs/tdsynnex' },
        { label: 'Ingram Micro Catalog', to: '/imprescatalogs/ingrammicro' },
        { label: 'Dell Catalog', to: '/imprescatalogs/dell' },
        { label: 'Carahsoft Catalog', to: '/imprescatalogs/carahsoft' },
      ],
    },
    {
      key: 'admin',
      label: 'Admin',
      icon: <AdminPanelSettingsOutlined />,
      links: [
        { label: 'Load Catalog', to: '/admin/loadCatalog' },
        ...(user.role.admin ? [{ label: 'Product Categories', to: '/admin/productCategories' }] : []),
      ],
    },
  ]

  const section = location.pathname.split('/')[1]
  const activeItem = items.find(item => item.key === flyout?.key)

  useEffect(() => {
    if (!props.open) {
      if (closeTimer.current) clearTimeout(closeTimer.current)
      focusOnOpen.current = false
      setFlyout(null)
    }
  }, [props.open])

  useEffect(() => {
    return () => {
      if (closeTimer.current) clearTimeout(closeTimer.current)
    }
  }, [])

  const cancelClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current)
  }

  const popperOptions = useMemo(
    () => ({
      onFirstUpdate: () => {
        if (focusOnOpen.current && flyoutNode.current) {
          focusOnOpen.current = false
          focusFirstLink(flyoutNode.current)
        }
      },
    }),
    [],
  )

  const openFlyout = (key: string, anchor: HTMLElement, viaKeyboard = false) => {
    cancelClose()
    focusOnOpen.current = false
    if (viaKeyboard) {
      if (flyout?.key === key && flyoutNode.current) focusFirstLink(flyoutNode.current)
      else focusOnOpen.current = true
    }
    setFlyout({ key, anchor })
  }

  const scheduleClose = () => {
    cancelClose()
    closeTimer.current = setTimeout(() => setFlyout(null), 150)
  }

  const closeFlyoutIfOther = (key: string) => {
    if (flyout && flyout.key !== key) setFlyout(null)
  }

  const handleFlyoutKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape' || e.key === 'ArrowLeft') {
      e.preventDefault()
      e.stopPropagation()
      const anchor = flyout?.anchor
      setFlyout(null)
      anchor?.focus()
      return
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      const links = Array.from(e.currentTarget.querySelectorAll<HTMLElement>('a[href]'))
      if (links.length === 0) return
      e.preventDefault()
      e.stopPropagation()
      const index = links.indexOf(document.activeElement as HTMLElement)
      const step = e.key === 'ArrowDown' ? 1 : -1
      links[(index + step + links.length) % links.length].focus()
    }
  }

  return (
    <Drawer
      open={props.open}
      onClose={props.onClose}
      elevation={8}
      slotProps={{ paper: { className: 'side-nav-paper' }, root: { disableEnforceFocus: true } }}
    >
      <img src={logo} alt="logo" className="side-nav-logo" />
      <MenuList className="side-nav-list" autoFocusItem={props.open}>
        {items.map(item => {
          const content = (
            <>
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText>{item.label}</ListItemText>
            </>
          )

          if (item.to) {
            return (
              <MenuItem
                key={item.key}
                className="side-nav-item"
                component={Link}
                to={item.to}
                selected={section === item.key}
                onClick={props.onClose}
                onMouseEnter={scheduleClose}
                onFocus={() => setFlyout(null)}
              >
                {content}
              </MenuItem>
            )
          }

          return (
            <MenuItem
              key={item.key}
              className="side-nav-item"
              selected={section === item.key}
              aria-haspopup="menu"
              aria-expanded={flyout?.key === item.key}
              onMouseEnter={e => openFlyout(item.key, e.currentTarget)}
              onMouseLeave={scheduleClose}
              onFocus={() => closeFlyoutIfOther(item.key)}
              onClick={e => openFlyout(item.key, e.currentTarget, e.detail === 0)}
              onKeyDown={e => {
                if (e.key === 'ArrowRight') {
                  e.preventDefault()
                  openFlyout(item.key, e.currentTarget, true)
                }
              }}
            >
              {content}
              <ChevronRight className="side-nav-item-chevron" />
            </MenuItem>
          )
        })}
      </MenuList>
      <Popper
        open={props.open && Boolean(flyout && activeItem)}
        anchorEl={flyout?.anchor}
        placement="right-start"
        popperOptions={popperOptions}
        className="side-nav-flyout"
      >
        <div
          ref={flyoutNode}
          role="presentation"
          onKeyDownCapture={handleFlyoutKeyDown}
          onClick={e => {
            if ((e.target as HTMLElement).closest('a[href]')) props.onClose()
          }}
        >
          {activeItem?.contractMenu && <ComplianceMenu open onMouseEnter={cancelClose} onMouseLeave={scheduleClose} />}
          {activeItem?.links && (
            <Paper
              className="side-nav-flyout-paper"
              elevation={8}
              onMouseEnter={cancelClose}
              onMouseLeave={scheduleClose}
            >
              <MenuList>
                {activeItem.links.map(link => (
                  <MenuItem key={link.to} component={Link} to={link.to} selected={location.pathname === link.to}>
                    {link.label}
                  </MenuItem>
                ))}
              </MenuList>
            </Paper>
          )}
        </div>
      </Popper>
    </Drawer>
  )
}
